import { useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Link2, Loader2, PackagePlus, PencilLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ProductsListPanel } from "@/components/products/products-list-panel";
import { CreateProductDialog } from "@/routes/workspace/$workspaceSlug/products/create-product-dialog";
import { logError } from "@/lib/structured-log";
import { searchWorkspaceProductsServer } from "@/server/products/search-workspace-products.server";
import { importProductsFromUrlsServer } from "@/server/products/import-products-from-urls.server";
import type { ProductListResponse } from "@/lib/api/generated/productListResponse";

export const Route = createFileRoute("/workspace/$workspaceSlug/knowledge/products")({
  staleTime: 10_000,
  gcTime: 2 * 60_000,
  loader: async ({ parentMatchPromise }) => {
    const parent = await parentMatchPromise;
    const workspaceId = parent?.loaderData?.workspaceId;

    if (!workspaceId || typeof workspaceId !== "string") {
      throw new Error("workspaceId not found in parent loader");
    }

    const productsData = (await searchWorkspaceProductsServer({
      data: {
        workspaceId,
        request: {
          page: 1,
          page_size: 20,
        },
      },
    })) as ProductListResponse;

    return {
      workspaceId,
      products: productsData.products || [],
      total: productsData.total || 0,
    };
  },
  component: KnowledgeProductsPage,
});

function KnowledgeProductsPage() {
  const { workspaceId, products, total } = Route.useLoaderData();
  const router = useRouter();
  const [showCreateEntry, setShowCreateEntry] = useState<boolean>(total === 0);

  const handleCreated = async (): Promise<void> => {
    await router.invalidate();
    setShowCreateEntry(false);
  };

  if (showCreateEntry) {
    return <ProductsCreateEntry workspaceId={workspaceId} onCreated={handleCreated} />;
  }

  return (
    <ProductsListPanel
      key={workspaceId}
      workspaceId={workspaceId}
      initialProducts={products}
      initialTotal={total}
    />
  );
}

interface ProductsCreateEntryProps {
  workspaceId: string;
  onCreated: () => void | Promise<void>;
}

function ProductsCreateEntry({ workspaceId, onCreated }: ProductsCreateEntryProps) {
  const { t, i18n } = useTranslation(["pages"]);
  const [urlsText, setUrlsText] = useState<string>("");
  const [isImporting, setIsImporting] = useState<boolean>(false);

  const handleImport = async (): Promise<void> => {
    if (isImporting) return;
    if (!urlsText.trim()) {
      toast.error(t("pages:products.createEntry.urlRequired"));
      return;
    }

    setIsImporting(true);
    try {
      const created = await importProductsFromUrlsServer({
        data: {
          workspaceId,
          urlsText,
          language: i18n.language,
        },
      });
      toast.success(t("pages:products.createEntry.importSuccess", { count: created.length }));
      await onCreated();
    } catch (error) {
      logError("products.url_import.frontend.failed", {
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error(t("pages:products.createEntry.importFailed"));
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="h-full overflow-auto bg-white px-6 py-8">
      <div className="mx-auto flex min-h-full w-full max-w-[900px] items-center">
        <div className="w-full">
          <div className="mb-7 text-center">
            <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-gray-950 text-white">
              <PackagePlus className="h-5 w-5" />
            </div>
            <h2 className="text-[20px] font-semibold text-gray-950">
              {t("pages:products.createEntry.title")}
            </h2>
            <p className="mx-auto mt-3 max-w-[560px] text-sm leading-6 text-gray-500">
              {t("pages:products.createEntry.description")}
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_260px]">
            <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700">
                  <Link2 className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-950">
                    {t("pages:products.createEntry.syncTitle")}
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-gray-500">
                    {t("pages:products.createEntry.syncDescription")}
                  </p>
                </div>
              </div>

              <Textarea
                value={urlsText}
                onChange={(event) => setUrlsText(event.target.value)}
                placeholder={t("pages:products.createEntry.urlPlaceholder")}
                className="min-h-[112px] resize-none rounded-lg border-gray-200 text-[13px] shadow-none focus-visible:ring-2 focus-visible:ring-gray-300"
              />

              <div className="mt-4 flex justify-end">
                <Button type="button" disabled={isImporting} onClick={() => void handleImport()}>
                  {isImporting ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("pages:products.createEntry.creating")}
                    </span>
                  ) : (
                    t("pages:products.createEntry.createProducts")
                  )}
                </Button>
              </div>
            </section>

            <CreateProductDialog workspaceId={workspaceId} onSuccess={onCreated}>
              <button
                type="button"
                className="h-full rounded-lg border border-gray-200 bg-gray-50 p-5 text-left transition hover:border-gray-300 hover:bg-white"
              >
                <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700">
                  <PencilLine className="h-4 w-4" />
                </div>
                <span className="block text-sm font-semibold text-gray-950">
                  {t("pages:products.createEntry.startManually")}
                </span>
                <span className="mt-2 block text-sm leading-5 text-gray-500">
                  {t("pages:products.createEntry.manualDescription")}
                </span>
              </button>
            </CreateProductDialog>
          </div>
        </div>
      </div>
    </div>
  );
}
